import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  CheckCircle, Globe, GraduationCap, Building, Users, Cpu, BookOpen,
  Microscope, Briefcase, ArrowRight, Star, Zap, Monitor, School
} from 'lucide-react';

const HERO_IMG1 = "https://images.unsplash.com/photo-1771765764892-91f2ed4ddbf4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxjbGFzc3Jvb20lMjBWUiUyMGhlYWRzZXRzJTIwc3R1ZGVudHMlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc4NjczMDQxOHww&ixlib=rb-4.1.0&q=85&w=800";
const HERO_IMG2 = "https://images.unsplash.com/photo-1771765856781-3adbac14d75b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxjbGFzc3Jvb20lMjBWUiUyMGhlYWRzZXRzJTIwc3R1ZGVudHMlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc4NjczMDQxOHww&ixlib=rb-4.1.0&q=85&w=800";

const k12Bundles = [
  {
    name: "Bronze",
    price: "$11,550",
    subtitle: "15-Student Starter Classroom",
    color: "from-amber-700/30 to-amber-900/10",
    border: "border-amber-600/40",
    badge: "bg-amber-700",
    includes: [
      "15 Quest 3S Headsets",
      "15 VXRLabs Licenses — 2 Years",
      "Customizable AI Lessons",
      "Online Professional Development",
      "Horizon Management Services",
    ],
    bestFor: "Classrooms, STEM labs & pilot programs",
  },
  {
    name: "Silver",
    price: "$22,175",
    subtitle: "25-Student Immersive Classroom",
    color: "from-slate-400/20 to-slate-600/10",
    border: "border-slate-400/40",
    badge: "bg-slate-500",
    includes: [
      "25 Quest 3S Headsets",
      "25 VXRLabs Licenses — 3 Years",
      "Customizable AI Lessons",
      "Online Professional Development",
      "Horizon Management Services",
    ],
    bestFor: "Schools & full classroom deployments",
  },
  {
    name: "Gold",
    price: "$44,350",
    subtitle: "50-Student Immersive Program",
    color: "from-yellow-500/20 to-yellow-700/10",
    border: "border-yellow-500/40",
    badge: "bg-yellow-600",
    featured: true,
    includes: [
      "50 Quest 3S Headsets",
      "50 VXRLabs Licenses — 3 Years",
      "Customizable AI Lessons",
      "Online Professional Development",
      "Horizon Management Services",
    ],
    bestFor: "Districts & multi-classroom programs",
  },
  {
    name: "Platinum",
    price: "$96,575",
    subtitle: "Advanced CTE + Science",
    color: "from-cyan-400/20 to-cyan-700/10",
    border: "border-cyan-400/40",
    badge: "bg-cyan-600",
    includes: [
      "50 Quest 3S Headsets",
      "50 VXRLabs Licenses — 2 Years",
      "50 CTE Licenses — 2 Years",
      "50 Dissection Licenses — 2 Years",
      "Customizable AI Lessons",
      "On-Site Professional Development",
      "Horizon Management Services",
    ],
    bestFor: "District-wide CTE, science & workforce programs",
  },
];

const higherEdBundles = [
  {
    name: "Starter",
    price: "$22,175",
    includes: [
      "25 Quest 3S Headsets",
      "25 VXRLabs Licenses — 3 Years",
      "Customizable AI Tutors",
      "Online Professional Development",
      "Horizon Management Services",
    ],
  },
  {
    name: "Simulator",
    price: "$47,175",
    includes: [
      "VR Simulator with AI",
      "25 Quest 3S Headsets",
      "25 VXRLabs Licenses — 3 Years",
      "Customizable AI Tutors",
      "Online Professional Development",
      "Horizon Management Services",
    ],
  },
  {
    name: "Digital Twin",
    price: "$99,850",
    featured: true,
    includes: [
      "Digital Twin Campus",
      "50 Quest 3S Headsets",
      "50 VXRLabs Licenses — 3 Years",
      "Customizable AI Tutors",
      "On-Site Professional Development",
      "Horizon Management Services",
    ],
  },
];

const serveList = [
  "K-12 Schools", "School Districts", "Colleges", "Universities",
  "CTE Programs", "Workforce Development", "Nonprofits", "Government Agencies",
  "Museums", "Libraries", "Community Organizations", "International Institutions",
];

const immersiveAccess = [
  { icon: Monitor, label: "VR Classrooms" },
  { icon: Cpu, label: "AI Learning & Tutors" },
  { icon: Microscope, label: "Science & Virtual Labs" },
  { icon: Briefcase, label: "CTE & Career Exploration" },
  { icon: BookOpen, label: "Virtual Dissection" },
  { icon: Zap, label: "Simulations" },
  { icon: Building, label: "Digital Twin Campuses" },
  { icon: GraduationCap, label: "Professional Development" },
];

function BundleCard({ bundle, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className={`relative p-6 rounded-2xl glass-light flex flex-col h-full border tech-bracket ${bundle.border || 'border-border'} ${bundle.featured ? 'ring-1 ring-cta/30' : ''}`}
      data-testid={`bundle-${bundle.name.toLowerCase()}`}
    >
      <div className={`inline-flex self-start px-3 py-1 rounded-full text-xs font-bold text-white mb-4 ${bundle.badge || 'bg-primary'}`}>
        {bundle.name}
      </div>
      <div className="text-3xl font-bold text-cta mb-1">{bundle.price}</div>
      {bundle.subtitle && <p className="text-sm text-muted-foreground mb-5">{bundle.subtitle}</p>}

      <ul className="space-y-2.5 flex-1 mb-5">
        {bundle.includes.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
            <span>{item}</span>
          </li>
        ))}
      </ul>

      {bundle.bestFor && (
        <p className="text-xs text-secondary italic mb-4">Best for: {bundle.bestFor}</p>
      )}

      <Link
        to="/contact"
        className="btn-primary w-full flex items-center justify-center gap-2 text-center"
        data-testid={`request-${bundle.name.toLowerCase()}-quote`}
      >
        Request {bundle.name} Quote <ArrowRight className="w-4 h-4" />
      </Link>
    </motion.div>
  );
}

export default function InternationalResellerPage() {
  return (
    <div className="min-h-screen pt-24" data-testid="reseller-page">

      {/* Hero */}
      <section className="py-12 md:py-20">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cta/20 text-cta mb-6">
                <Globe className="w-4 h-4" />
                <span className="text-sm font-medium">International Reseller</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-serif font-bold text-white mb-4" data-testid="reseller-title">
                Immersive Education Solutions
              </h1>
              <p className="text-lg text-muted-foreground mb-6">
                Bring Virtual Reality, AI, immersive learning, CTE, and Digital Twin technology into your school, university, or organization.
              </p>
              <p className="text-muted-foreground mb-4">
                The Matrix University provides scalable VictoryXR / VXRLabs immersive education solutions for K-12 schools, colleges, universities, nonprofits, workforce programs, and educational organizations worldwide.
              </p>
              <p className="text-cta font-semibold text-lg mb-8">From one classroom to an entire virtual campus.</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2">
                  Request a Quote <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/contact" className="btn-secondary inline-flex items-center justify-center gap-2">
                  Schedule a Consultation
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-2xl overflow-hidden shadow-2xl shadow-primary/10">
                  <img src={HERO_IMG1} alt="VR classroom education" className="w-full h-48 md:h-64 object-cover" />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-2xl shadow-primary/10 mt-8">
                  <img src={HERO_IMG2} alt="Students with VR headsets" className="w-full h-48 md:h-64 object-cover" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* K-12 Classroom Bundles */}
      <section className="py-12 md:py-16 bg-card/50">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 text-primary mb-4">
              <School className="w-4 h-4" />
              <span className="text-sm font-medium">K-12 Solutions</span>
            </div>
            <h2 className="text-2xl md:text-4xl font-serif font-bold text-white mb-3">K-12 Classroom Bundles</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Scalable immersive education packages from starter classrooms to district-wide programs.</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {k12Bundles.map((b, i) => <BundleCard key={b.name} bundle={b} index={i} />)}
          </div>
        </div>
      </section>

      {/* Higher Education */}
      <section className="py-12 md:py-16">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/20 text-secondary mb-4">
              <GraduationCap className="w-4 h-4" />
              <span className="text-sm font-medium">Higher Education</span>
            </div>
            <h2 className="text-2xl md:text-4xl font-serif font-bold text-white mb-3">Higher Education Solutions</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">From lab environments to full Digital Twin campuses.</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {higherEdBundles.map((b, i) => (
              <motion.div
                key={b.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className={`p-6 rounded-2xl glass-light flex flex-col h-full ${b.featured ? 'ring-1 ring-cta/30' : ''}`}
                data-testid={`higher-ed-${b.name.toLowerCase().replace(' ', '-')}`}
              >
                {b.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 rounded-full bg-cta text-black text-xs font-bold flex items-center gap-1">
                      <Star className="w-3 h-3" /> Premium
                    </span>
                  </div>
                )}
                <h3 className="text-xl font-serif font-bold text-white mb-1">{b.name}</h3>
                <div className="text-2xl font-bold text-cta mb-4">{b.price}</div>
                <ul className="space-y-2.5 flex-1 mb-5">
                  {b.includes.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className="btn-primary w-full flex items-center justify-center gap-2 text-center">
                  Request a Quote <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Immersive Access */}
      <section className="py-12 md:py-16 bg-card/50">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-3">Immersive Learning. Real-World Impact.</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Give learners access to immersive:</p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {immersiveAccess.map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="p-4 rounded-xl glass-light text-center">
                <item.icon className="w-8 h-8 text-cta mx-auto mb-2" />
                <p className="text-white font-medium text-sm">{item.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-12 md:py-16">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-3">Who We Serve</h2>
          </motion.div>
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {serveList.map((item, i) => (
              <motion.span key={i} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}
                className="px-4 py-2 rounded-full glass-light text-sm text-muted-foreground border border-border">
                {item}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16 bg-card/50">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-3xl mx-auto text-center p-8 md:p-12 rounded-2xl glass-light">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-4">Ready to Build Your Immersive Program?</h2>
            <p className="text-lg text-muted-foreground mb-4">
              Whether you're starting with 15 headsets or building an entire Digital Twin Campus, The Matrix University can help identify the right immersive education solution for your organization.
            </p>
            <p className="text-sm text-secondary font-medium mb-8">THE MATRIX UNIVERSITY — Education • Innovation • Immersive Technology</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2">
                Request a Quote <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/contact" className="btn-secondary inline-flex items-center justify-center gap-2">
                Contact The Matrix University
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
